/**
 * TestedAssetPreviewDialog — reads one tested asset at a time without leaving the
 * report.
 *
 * A left rail lists every asset in item-letter order; the right pane shows the
 * selected one: its media frame (never stretched, `object-contain` on a bordered
 * surface, the QuickStudyCreativeCarousel pattern), then the name, description and
 * the type-specific rows. A row exists only when its field is present, so a
 * text-only concept never renders an empty "Headline" label.
 *
 * Type narrowing goes through `asset.type` and nothing else: the dialog is generic
 * over every `TestedAsset` variant the questionnaire contract defines, so a study
 * whose assets are ads, packs or pain points all read through the same component.
 *
 * Arrow keys step the selection. The listener sits on `DialogContent` rather than
 * on `window`: Radix focuses the content on open, so the keys reach us there and
 * never leak to the page behind the overlay. Escape stays Radix's.
 */
import { KeyboardEvent, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight } from 'lucide-react'

import { CreativeLightbox } from '@/components/chat/CreativeLightbox'
import { CreativeMedia, isVideoCreativeLike } from '@/components/chat/CreativeMedia'
import { ScrollableImagePreview } from '@/components/media/ScrollableImagePreview'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  assetCountLabelKey,
  assetLabel,
  resolveAssetMediaUrl,
  sortAssetsByItemTag,
} from '@/features/studies/report/utils/testedAssets'
import { cn } from '@/lib/utils'

import {
  AssetMediaRef,
  PriceWording,
  TestedAsset,
  formatTypedPrice,
  respondentWordingFor,
} from '@digital-twins/shared-types'
import { useContentLanguage } from '@/i18n/contentLanguageContext'

/**
 * A field label is built from a field name while `t` is typed against the
 * generated key union, so this table states what the code already guarantees
 * instead of loosening the call site. A new field means a line here; tsc
 * rejects a key that is not in the generated union.
 */
const ASSET_FIELD_LABEL_KEYS = {
  headline: 'studyReport.assetField.headline',
  bodyCopy: 'studyReport.assetField.bodyCopy',
  callToAction: 'studyReport.assetField.callToAction',
  placement: 'studyReport.assetField.placement',
  proposition: 'studyReport.assetField.proposition',
  targetUser: 'studyReport.assetField.targetUser',
  category: 'studyReport.assetField.category',
  price: 'studyReport.assetField.price',
  pageUrl: 'studyReport.assetField.pageUrl',
  primaryCallToAction: 'studyReport.assetField.primaryCallToAction',
  aboveFoldSummary: 'studyReport.assetField.aboveFoldSummary',
} as const

type AssetField = keyof typeof ASSET_FIELD_LABEL_KEYS

/** One label/value line under the media frame. `field` keys the i18n label. */
interface AssetFieldRowData {
  field: AssetField
  value: string
}

/** A media entry on the asset whose url resolved, kept with that url. */
interface ResolvedAssetMedia {
  media: AssetMediaRef
  url: string
}

/** A field worth a row: present, a string, and not just whitespace. */
function hasText(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * The type-specific text rows, in reading order. Every variant of the union is
 * covered through `asset.type`; a variant with nothing but name and description
 * (`pain`) contributes no rows at all.
 */
function assetFieldRows(asset: TestedAsset, wording: PriceWording): Array<AssetFieldRowData> {
  const rows: Array<AssetFieldRowData> = []

  const push = (field: AssetField, value: string | undefined) => {
    if (hasText(value)) {
      rows.push({ field, value })
    }
  }

  if (asset.type === 'ad') {
    push('headline', asset.headline)
    push('bodyCopy', asset.bodyCopy)
    push('callToAction', asset.callToAction)
    push('placement', asset.placement)
  }

  if (asset.type === 'concept' || asset.type === 'brand') {
    push('proposition', asset.proposition)
    push('targetUser', asset.targetUser)
  }

  if (asset.type === 'product') {
    if (asset.price) {
      push('price', formatTypedPrice(asset.price, wording))
    }
    push('category', asset.category)
  }

  if (asset.type === 'landing_page') {
    push('headline', asset.headline)
    push('primaryCallToAction', asset.primaryCallToAction)
    push('aboveFoldSummary', asset.aboveFoldSummary)
  }

  return rows
}

/** Every media entry on the asset whose url resolves, in the asset's own order. */
function resolvableMedia(asset: TestedAsset): Array<ResolvedAssetMedia> {
  const resolved: Array<ResolvedAssetMedia> = []
  for (const media of asset.media) {
    const url = resolveAssetMediaUrl(media)
    if (url) {
      resolved.push({ media, url })
    }
  }
  return resolved
}

interface AssetFieldRowProps {
  label: string
  children: ReactNode
}

function AssetFieldRow(props: AssetFieldRowProps) {
  const { label, children } = props

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  )
}

export interface TestedAssetPreviewDialogProps {
  assets: Array<TestedAsset>
  itemTags: Record<string, string>
  initialAssetId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TestedAssetPreviewDialog(props: TestedAssetPreviewDialogProps) {
  const { assets, itemTags, initialAssetId, open, onOpenChange } = props

  const [selectedId, setSelectedId] = useState<string | undefined>(initialAssetId)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const { t } = useTranslation('app')
  // A price reads in the study's content language, as respondents read it.
  const priceWording = respondentWordingFor(useContentLanguage())

  const sortedAssets = sortAssetsByItemTag(assets, itemTags)
  const firstAssetId = sortedAssets[0]?.id

  // Re-open on a different asset and the pane follows: the stack hands a new
  // `initialAssetId` at the same moment `open` flips, and a stale selection from
  // the previous opening would show the wrong asset.
  useEffect(() => {
    if (!open) {
      return
    }
    setSelectedId(initialAssetId || firstAssetId)
    setLightboxIndex(null)
  }, [open, initialAssetId, firstAssetId])

  const selectedIndex = Math.max(
    0,
    sortedAssets.findIndex((asset) => asset.id === selectedId),
  )
  const selected = sortedAssets[selectedIndex]

  const select = (assetId: string) => {
    setSelectedId(assetId)
    // The lightbox indexes into the PREVIOUS asset's media; carrying it across a
    // selection change would open a viewer on the wrong creative.
    setLightboxIndex(null)
  }

  const step = (direction: -1 | 1) => {
    const count = sortedAssets.length
    if (count === 0) {
      return
    }
    const next = sortedAssets[(selectedIndex + direction + count) % count]
    select(next.id)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp'
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown'
    if (!back && !forward) {
      return
    }
    event.preventDefault()
    step(back ? -1 : 1)
  }

  if (sortedAssets.length === 0 || !selected) {
    return null
  }

  const countKey = assetCountLabelKey(assets)
  const title = t(countKey, { count: assets.length })
  const media = resolvableMedia(selected)
  const isMessage = selected.type === 'message'
  // A message's words are the asset, so they print below; a picture attached to
  // it (a pack shot beside the claim) is part of what was shown, so it has a frame.
  const frame = media[0]
  // A landing page is one tall capture: it scrolls inside its frame, never shrunk to a sliver.
  const isPageFrame = Boolean(frame) && selected.type === 'landing_page'
  const isVideoFrame = Boolean(frame) && !isPageFrame && isVideoCreativeLike({ mimeType: frame?.media.mimeType, src: frame?.url })
  const rows = assetFieldRows(selected, priceWording)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="report-asset-dialog"
        aria-describedby={undefined}
        onKeyDown={onKeyDown}
        className="max-w-4xl"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="mt-5 grid gap-6 md:grid-cols-[220px_1fr]">
          <div className="flex max-h-[480px] flex-col gap-1 overflow-y-auto">
            {sortedAssets.map((asset, idx) => {
              const isSelected = asset.id === selected.id
              return (
                <button
                  key={asset.id}
                  type="button"
                  data-testid={`report-asset-rail-${idx}`}
                  aria-current={isSelected ? 'true' : undefined}
                  onClick={() => select(asset.id)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isSelected && 'bg-muted/40',
                  )}
                >
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted/40 text-[11px] font-bold text-foreground/80">
                    {itemTags[asset.id] || String(idx + 1)}
                  </span>
                  <span className="truncate text-sm">{assetLabel(asset, priceWording)}</span>
                </button>
              )
            })}
          </div>

          <div data-testid="report-asset-pane" className="flex min-w-0 flex-col gap-4">
            {frame && isPageFrame && (
              <div
                data-testid="report-asset-media"
                className="rounded-xl border border-border/50 bg-muted/20 p-2"
              >
                <ScrollableImagePreview
                  src={frame.url}
                  alt={frame.media.altText || selected.name}
                  title={selected.name}
                  thumbnailClassName="h-64 w-full"
                />
              </div>
            )}

            {frame && isVideoFrame && (
              <div data-testid="report-asset-media" className="flex flex-col gap-2">
                {/*
                  Video plays inline with native controls so the reader can watch
                  without leaving the dialog; CreativeMedia's muted poster element
                  is for tiles. Full screen stays one click away via the lightbox.

                  The frame is a fixed 16:9 box rather than the video's own box: a
                  video element has no intrinsic size until its metadata arrives,
                  so sizing to the element makes the dialog jump taller mid-load.
                  `object-contain` keeps any other ratio letterboxed inside it.
                */}
                <div className="aspect-video max-h-[420px] w-full overflow-hidden rounded-xl border border-border/50 bg-muted/20">
                  <video
                    src={frame.url}
                    controls
                    playsInline
                    preload="metadata"
                    aria-label={frame.media.altText || selected.name}
                    data-testid="report-asset-video"
                    className="h-full w-full object-contain"
                  />
                </div>
                <button
                  type="button"
                  data-testid="report-asset-full-screen"
                  onClick={() => setLightboxIndex(media.indexOf(frame))}
                  className="self-end text-xs text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('studyReport.assetOpenFullScreen')}
                </button>
              </div>
            )}

            {frame && !isPageFrame && !isVideoFrame && (
              <button
                type="button"
                data-testid="report-asset-media"
                aria-label={t('studyReport.assetOpenFullScreen')}
                onClick={() => setLightboxIndex(media.indexOf(frame))}
                className="block w-full overflow-hidden rounded-xl border border-border/50 bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <CreativeMedia
                  src={frame.url}
                  mimeType={frame.media.mimeType}
                  alt={frame.media.altText || selected.name}
                  className="max-h-[420px] w-full object-contain"
                />
              </button>
            )}

            {isMessage && (
              <Text
                size="lg"
                data-testid="report-asset-message-text"
                className="font-display text-foreground"
              >
                “{selected.messageText}”
              </Text>
            )}

            {(!isMessage || rows.length > 0) && (
            <div className="flex flex-col gap-3">
              {!isMessage && (
                <Heading level={3} size="base" weight="semibold" data-testid="report-asset-name">
                  {assetLabel(selected, priceWording)}
                </Heading>
              )}

              {!isMessage && hasText(selected.description) && (
                <Text size="sm" color="muted" data-testid="report-asset-description">
                  {selected.description}
                </Text>
              )}

              {selected.type === 'landing_page' && hasText(selected.pageUrl) && (
                <AssetFieldRow label={t(ASSET_FIELD_LABEL_KEYS.pageUrl)}>
                  <a
                    href={selected.pageUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="report-asset-page-url"
                    className="underline underline-offset-2"
                  >
                    {selected.pageUrl}
                  </a>
                </AssetFieldRow>
              )}

              {rows.map((row) => (
                <AssetFieldRow
                  key={row.field}
                  label={t(ASSET_FIELD_LABEL_KEYS[row.field])}
                >
                  <span data-testid={`report-asset-field-${row.field}`}>{row.value}</span>
                </AssetFieldRow>
              ))}
            </div>
            )}

            {sortedAssets.length > 1 && (
            <div className="mt-auto flex items-center gap-2 pt-2">
              <button
                type="button"
                data-testid="report-asset-prev"
                aria-label={t('studyReport.assetPrev')}
                onClick={() => step(-1)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-muted/40 text-foreground/80 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="report-asset-next"
                aria-label={t('studyReport.assetNext')}
                onClick={() => step(1)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-muted/40 text-foreground/80 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
              <span data-testid="report-asset-counter" className="text-xs text-muted-foreground">
                {selectedIndex + 1} / {sortedAssets.length}
              </span>
            </div>
            )}
          </div>
        </div>

        {lightboxIndex !== null && media.length > 0 && (
          <CreativeLightbox
            creatives={media.map((entry) => ({
              src: entry.url,
              mimeType: entry.media.mimeType,
            }))}
            index={lightboxIndex}
            onIndex={setLightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
